import type { ReactNode } from 'react';
import {
  Body1,
  Body1Strong,
  Button,
  Caption1,
  Caption1Strong,
  Subtitle2,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { useIsNarrowViewport } from '../hooks/useIsNarrowViewport';
import { useResponsiveStyles } from '../hooks/useResponsiveStyles';
import { BORDER, LAYOUT, SPACE, SPACE_X } from '../styles/scale';
import { useA11yStyles, useSurfaceStyles } from '../styles/surfaces';

/**
 * One column definition, two faces (#202).
 *
 * `DataCardList`'s docblock already claimed the contract this component finally
 * enforces — "reuses the exact column headings as card labels so the two
 * presentations describe the same data in the same words" — but nothing made it
 * true: every page declared its `<TableHeaderCell>`s in one branch and its card
 * labels in another, by hand, and the two drifted the moment a column was added
 * to only one of them. Here a page declares `columns` ONCE; the table below
 * 700px is not restyled, it is replaced by cards derived from the same array,
 * so a column that exists in one face cannot fail to exist in the other.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE BUG THIS EXISTS TO KILL: SILENT CLIPPING
 * ─────────────────────────────────────────────────────────────────────────────
 * Measured in a real browser, not inferred. At a 1440px viewport the Dashboard's
 * table wanted 1430px of content inside a 1375px box; at 900px — a Teams tab
 * with the side chat open, an everyday configuration — 115px was cut off. The
 * Actions menu was literally unreachable, and the page reported ZERO horizontal
 * overflow, which is why nobody ever saw a scrollbar and nobody ever filed it.
 *
 * The mechanism is a two-part trap, and both halves are undone here:
 *
 *  1. **Fluent's `Table` sets `table-layout: fixed` and `width: 100%`.** Under
 *     fixed layout the table is pinned to exactly its container's width and the
 *     columns squash; content that no longer fits its cell overflows the CELL,
 *     not the table. The table itself is still exactly 100% wide — which is
 *     precisely why the document measured no overflow at all.
 *  2. **`useSurfaceStyles().tableCard` sets `overflow: hidden`**, correctly, so
 *     the table's square corners cannot poke past the card's 8px radius. It also
 *     therefore cut the overflowing cell content off with nothing to scroll.
 *
 * So: `tableLayout: 'auto'` lets the table grow to the width its content
 * genuinely needs, and a dedicated scroll element between the card and the table
 * takes `overflow-x: auto`. The card keeps its rounded corners and its
 * `overflow: hidden`; the scroll element inside it is what absorbs the excess.
 * Content is now always reachable, at every viewport, by construction —
 * `DataTable.test.tsx` asserts that rule off the injected stylesheet, because
 * jsdom has no layout engine and would report 0 for every width.
 *
 * ── Why not drop low-priority columns instead? ──
 * Dropping a column removes data from the screen without telling anyone, which
 * is the SAME class of defect as clipping it — a user who cannot see the total
 * is no better off for the fact that we meant it. Scrolling is the guarantee;
 * `priority` is therefore not a drop list but an importance ORDER, and it is
 * spent where space is genuinely finite: the mobile card, where the
 * highest-priority fields lead so the top of the card answers the question the
 * page is for.
 */

/** The agreed row density. 44px is on the 4px grid and is also the mobile touch minimum, so one number serves both. Fluent's own `size="medium"` `TableCell` resolves to exactly this; the header band is matched to it here. */
export const ROW_HEIGHT = '44px';

/** Sorted-column indicator. A glyph, never a colour: `teamsHighContrastTheme` discards the brand hue entirely, so an arrow (paired with `aria-sort`) is the only signal that survives all three themes. */
const SORT_GLYPH = { asc: '↑', desc: '↓' } as const;

const ARIA_SORT = { asc: 'ascending', desc: 'descending' } as const;

export interface Column<T> {
  /** Stable identity, and what `onSort` is called with. */
  key: string;
  /** The desktop header AND the mobile card's field label — one string, so the two faces cannot describe the same data in different words. */
  header: string;
  /** `'end'` right-aligns the cell and its header together; anything else is start-aligned. */
  align?: 'start' | 'end';
  /**
   * Money and quantities. Adds `font-variant-numeric: tabular-nums` and, unless
   * `align` explicitly says otherwise, end-alignment — wireframe X1: "right
   * aligned with tabular figures, so a column of numbers compares by eye".
   *
   * Deliberately NOT `fontFamilyNumeric`, which is Bahnschrift in this theme and
   * would change the typeface rather than just the figure widths.
   */
  numeric?: boolean;
  /** A CSS width for the column. Dynamic per caller, so it is the one inline style here. */
  width?: string;
  /** Importance, high wins. Orders the mobile card's fields; see the class docblock for why it is not a drop list. */
  priority?: number;
  render: (row: T) => ReactNode;
  /** Present in the mobile card only — for a field the desktop already shows inside another column. */
  cardOnly?: boolean;
  /**
   * Present in the desktop table only.
   *
   * For a column the mobile card already carries in its TITLE. Without this, a
   * card headed "PR-151" also renders a "Request # · PR-151" field beneath it —
   * the same value twice, which reads as a rendering fault rather than as
   * emphasis. Found by rendering the card at 390px; nothing in jsdom can see a
   * value repeated in two places.
   */
  tableOnly?: boolean;
  /** Renders its header as a sort control. Requires `onSort`; without one the column is inert rather than a button that does nothing. */
  sortable?: boolean;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  /**
   * Pointer convenience only, and desktop only. A row is not a focusable
   * control, so the keyboard path is the real `Link`/`Button` a caller renders
   * inside its first column — the pattern wireframe sheet 10 (K1) requires and
   * the Dashboard already follows. On the mobile card the same rule applies to
   * `cardTitle`.
   */
  onRowClick?: (row: T) => void;
  rowActions?: (row: T) => ReactNode;
  sort?: { key: string; order: 'asc' | 'desc' };
  onSort?: (key: string) => void;
  /** The table's accessible name, rendered as a real (visually hidden) `<caption>` — the element the HTML accessibility mapping names a table from. */
  caption: string;
  /** The mobile card's lead line: the row's identity, the way the first column is on desktop. */
  cardTitle: (row: T) => ReactNode;
}

const useStyles = makeStyles({
  /**
   * The element that makes clipping impossible. It sits INSIDE `tableCard`, so
   * the card keeps both its 8px radius and the `overflow: hidden` that stops the
   * table's square corners showing through — this one scrolls instead of cutting.
   */
  scroll: {
    overflowX: 'auto',
    // The card is the vertical rhythm; only the horizontal axis is in question.
    overflowY: 'hidden',
  },

  /**
   * `auto`, overriding Fluent's `fixed`. Fixed layout is what pinned the table
   * to its container and pushed the overflow down into the cells, where the
   * card's `overflow: hidden` swallowed it without a scrollbar anywhere.
   */
  table: {
    tableLayout: 'auto',
    width: '100%',
  },

  /** Sheet 08 X1: a header band, not bold black text — bold black competes with the data it labels. */
  headerRow: {
    borderBottom: BORDER.divider,
  },
  headerCell: {
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground2,
    height: ROW_HEIGHT,
    padding: `${SPACE.none} ${SPACE_X.m}`,
    // A header that wraps re-flows the whole column; the scroll element above
    // is what absorbs the width instead.
    whiteSpace: 'nowrap',
  },

  /**
   * The body cell (#211). Vertical padding so a row reads with breathing room
   * rather than pinned to Fluent's own 44px `size="medium"` height with zero
   * padding of its own — `height: 'auto'` overrides that fixed height (the
   * same override technique already proven on `padding` above it, entry-57
   * style) so a wrapped, multi-line cell can grow the row instead of clipping
   * against it. `minHeight` keeps the 44px touch target as a floor for a
   * single short line.
   *
   * `maxWidth`/`overflowWrap`/`wordBreak` are what make wrapping possible at
   * all: `DataTable`'s `table` is `table-layout: auto` (#202), which sizes a
   * column to its content's natural width with nothing to make it wrap. The
   * cap is `LAYOUT.cellWrap` — see its own docblock for why 320px only ever
   * engages the columns that were genuinely cramped.
   */
  cell: {
    height: 'auto',
    minHeight: ROW_HEIGHT,
    paddingTop: SPACE.m,
    paddingBottom: SPACE.m,
    paddingLeft: SPACE_X.m,
    paddingRight: SPACE_X.m,
    maxWidth: LAYOUT.cellWrap,
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },

  /**
   * Center alignment, on the cell AND its header — the default for both, so
   * the two always agree (#211). Same descendant-rule shape as `alignEnd`
   * below: `TableHeaderCell` puts its children inside a flex
   * `.fui-TableHeaderCell__button` div, so `text-align` alone moves the `<td>`
   * and leaves the `<th>` looking un-aligned beside it.
   */
  alignCenter: {
    textAlign: 'center',
    '& .fui-TableHeaderCell__button': { justifyContent: 'center' },
  },

  /**
   * The explicit left-align escape hatch a caller reaches for with
   * `align: 'start'`. Kept as its own styled class, same shape as
   * `alignCenter`/`alignEnd`, rather than left unstyled and relying on the
   * browser's own initial `text-align` — an unstyled cell is indistinguishable
   * from one nobody thought about, and `alignClass` below needs a real class
   * to return so `align: 'start'` still means something once `alignCenter`
   * is the default for an unset `align`.
   */
  alignStart: {
    textAlign: 'start',
    '& .fui-TableHeaderCell__button': { justifyContent: 'flex-start' },
  },

  /**
   * End alignment, on the cell AND its header. `TableHeaderCell` puts its
   * children inside a flex `.fui-TableHeaderCell__button` div, so `text-align`
   * alone moves the `<td>` and leaves the `<th>` looking un-aligned beside it —
   * the descendant rule is what actually keeps the pair together.
   */
  alignEnd: {
    textAlign: 'end',
    '& .fui-TableHeaderCell__button': { justifyContent: 'flex-end' },
  },

  numeric: {
    fontVariantNumeric: 'tabular-nums',
  },

  /** Only on a row that genuinely navigates: the hover IS the affordance, so promising one that does not exist is a lie. */
  clickableRow: {
    cursor: 'pointer',
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },

  /**
   * A transparent `Button` reset to read as header text. Fluent's `Button`
   * rather than a raw `<button>` deliberately: a native form element does not
   * inherit `color` from `FluentProvider` and falls back to the browser's
   * `buttontext` system colour, which rendered the Dashboard's stat tiles as
   * blank boxes in high contrast (CLAUDE.md mistake-log entry 54). The explicit
   * `color` here is the header's own `Foreground2`, not the default.
   */
  sortButton: {
    minWidth: 'auto',
    padding: SPACE.none,
    color: tokens.colorNeutralForeground2,
    columnGap: SPACE_X.xs,
  },
  /** Reserved whether or not this column is the sorted one, so clicking a header does not shunt every other header sideways. */
  sortGlyph: {
    display: 'inline-block',
    minWidth: SPACE_X.m,
    textAlign: 'center',
  },

  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE.l,
    listStyleType: 'none',
    padding: SPACE.none,
    margin: SPACE.none,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE.s,
  },
  /** Fluent's `Text` is `display: inline`; `as="h3"` alone gives the right role and the wrong box. */
  cardHeading: {
    display: 'block',
  },
  cardFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE.xs,
  },
  cardField: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: SPACE_X.m,
  },
  cardLabel: {
    color: tokens.colorNeutralForeground2,
    flexShrink: '0',
  },
  /** Wraps rather than widens: a long description must never push the card past the canvas, which is the failure mode cards exist to avoid. */
  cardValue: {
    textAlign: 'end',
    overflowWrap: 'anywhere',
  },
  cardActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: SPACE_X.s,
  },
});

/**
 * Fluent's typography components render a `<span>`, so wrapping an arbitrary
 * `ReactNode` in one would put a caller's `<div>` (a `Menu`, a stacked cell)
 * inside a span — invalid markup that React will not warn about. Plain text is
 * given the body ramp; anything that is already a component brings its own.
 */
function isPlainText(content: ReactNode): content is string | number {
  return typeof content === 'string' || typeof content === 'number';
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  rowActions,
  sort,
  onSort,
  caption,
  cardTitle,
}: DataTableProps<T>) {
  const styles = useStyles();
  const surfaces = useSurfaceStyles();
  const a11y = useA11yStyles();
  const responsive = useResponsiveStyles();
  const isNarrow = useIsNarrowViewport();

  /** `numeric` implies end alignment unless a caller says otherwise — a right-aligned column of figures is the whole point of marking one numeric. */
  function isEndAligned(column: Column<T>): boolean {
    if (column.align !== undefined) return column.align === 'end';
    return column.numeric === true;
  }

  /**
   * Center is the default for a column that asks for neither end nor start
   * alignment (#211) — vendor, company, status, the request's own identity.
   * An explicit `align: 'start'` is still honoured as a genuine left-align
   * escape hatch rather than folded into the new default, so it keeps
   * meaning what its own docblock says it means.
   */
  function alignClass(column: Column<T>): string {
    if (isEndAligned(column)) return styles.alignEnd;
    if (column.align === 'start') return styles.alignStart;
    return styles.alignCenter;
  }

  function ariaSortFor(column: Column<T>): 'ascending' | 'descending' | 'none' | undefined {
    if (column.sortable !== true || onSort === undefined) return undefined;
    if (sort?.key === column.key) return ARIA_SORT[sort.order];
    return 'none';
  }

  if (isNarrow) {
    // Sheet 08/S1a: below the breakpoint a table is REPLACED by cards, never
    // scrolled sideways — Teams forbids scrolling the tab canvas, and a
    // two-cell row of label and value in one column is a list item, which is
    // what lets a screen reader announce a label beside its value instead of
    // replaying a column header from far off-screen.
    const cardColumns = columns
      .filter((column) => column.tableOnly !== true)
      .toSorted((a, b) => (b.priority ?? 0) - (a.priority ?? 0));

    return (
      <ul aria-label={caption} className={mergeClasses(styles.cardList, responsive.touchTargets)}>
        {rows.map((row) => (
          <li key={rowKey(row)} className={mergeClasses(surfaces.card, styles.card)}>
            <Subtitle2 as="h3" className={styles.cardHeading}>
              {cardTitle(row)}
            </Subtitle2>
            <div className={styles.cardFields}>
              {cardColumns.map((column) => {
                const content = column.render(row);
                return (
                  <div key={column.key} className={styles.cardField}>
                    <Caption1 className={styles.cardLabel}>{column.header}</Caption1>
                    <span
                      className={mergeClasses(
                        styles.cardValue,
                        column.numeric === true ? styles.numeric : undefined,
                      )}
                    >
                      {isPlainText(content) ? <Body1Strong>{content}</Body1Strong> : content}
                    </span>
                  </div>
                );
              })}
            </div>
            {rowActions && <div className={styles.cardActions}>{rowActions(row)}</div>}
          </li>
        ))}
      </ul>
    );
  }

  const tableColumns = columns.filter((column) => column.cardOnly !== true);

  return (
    <div className={surfaces.tableCard}>
      <div className={styles.scroll}>
        <Table className={styles.table} size="medium">
          {/* A real `<caption>`, not `aria-label`: it is the element the HTML
              accessibility mapping names a table from, and it survives being
              copied out of the page. Hidden because every page already renders
              its own visible heading above the card. */}
          <caption className={a11y.visuallyHidden}>{caption}</caption>
          <TableHeader>
            <TableRow className={styles.headerRow}>
              {tableColumns.map((column) => {
                const active = sort?.key === column.key;
                const cellClass = mergeClasses(
                  styles.headerCell,
                  alignClass(column),
                  column.numeric === true ? styles.numeric : undefined,
                );

                return (
                  <TableHeaderCell
                    key={column.key}
                    aria-sort={ariaSortFor(column)}
                    className={cellClass}
                    style={column.width === undefined ? undefined : { width: column.width }}
                  >
                    {column.sortable === true && onSort !== undefined ? (
                      <Button
                        appearance="transparent"
                        className={styles.sortButton}
                        onClick={() => {
                          onSort(column.key);
                        }}
                      >
                        <Caption1Strong>{column.header}</Caption1Strong>
                        {/* Decorative: `aria-sort` on the cell already tells a
                            screen reader which way this column is sorted, so
                            announcing the arrow would only repeat it. */}
                        <span aria-hidden="true" className={styles.sortGlyph}>
                          {active && sort ? SORT_GLYPH[sort.order] : ''}
                        </span>
                      </Button>
                    ) : (
                      <Caption1Strong>{column.header}</Caption1Strong>
                    )}
                  </TableHeaderCell>
                );
              })}
              {rowActions && (
                <TableHeaderCell className={mergeClasses(styles.headerCell, styles.alignCenter)}>
                  <span className={a11y.visuallyHidden}>Actions</span>
                </TableHeaderCell>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={rowKey(row)}
                className={onRowClick ? styles.clickableRow : undefined}
                onClick={
                  onRowClick
                    ? () => {
                        onRowClick(row);
                      }
                    : undefined
                }
              >
                {tableColumns.map((column) => {
                  const content = column.render(row);
                  return (
                    <TableCell
                      key={column.key}
                      className={mergeClasses(
                        styles.cell,
                        alignClass(column),
                        column.numeric === true ? styles.numeric : undefined,
                      )}
                    >
                      {isPlainText(content) ? <Body1>{content}</Body1> : content}
                    </TableCell>
                  );
                })}
                {rowActions && (
                  // Stops a menu item or an inline button from also firing the
                  // row's own navigation underneath it.
                  <TableCell
                    className={mergeClasses(styles.cell, styles.alignCenter)}
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                  >
                    {rowActions(row)}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
